import { motion } from "framer-motion";
import { Building2, Rocket, ShoppingBag, TrendingUp } from "lucide-react";

const WhoWeWorkWithPage = () => {
  const segments = [
    {
      icon: <Building2 size={48} />,
      title: "MSMEs & SMBs",
      description: "Established businesses looking to modernize their marketing approach and scale systematically.",
      challenges: [
        "Limited marketing resources and bandwidth",
        "Need for predictable, ROI-driven campaigns",
        "Transitioning from offline to digital-first",
        "Building brand presence in competitive markets"
      ],
      solutions: [
        "Cost-effective AI automation setup",
        "Clear strategy roadmaps with measurable outcomes",
        "Hands-on execution support",
        "Training for internal teams"
      ]
    },
    {
      icon: <Rocket size={48} />,
      title: "Funded Startups",
      description: "High-growth startups needing strategic marketing to complement their product innovation.",
      challenges: [
        "Rapid scaling without marketing chaos",
        "Need for data-driven growth strategies",
        "Building brand authority in new markets",
        "Optimizing customer acquisition costs"
      ],
      solutions: [
        "Growth-focused strategic frameworks",
        "AI-powered market intelligence",
        "Performance marketing optimization",
        "Scalable automation systems"
      ]
    },
    {
      icon: <ShoppingBag size={48} />,
      title: "D2C Brands",
      description: "Direct-to-consumer brands seeking to build loyal communities and predictable revenue streams.",
      challenges: [
        "Standing out in crowded digital marketplaces",
        "Building authentic brand narratives",
        "Optimizing customer lifetime value",
        "Managing multi-channel presence"
      ],
      solutions: [
        "Brand storytelling and positioning",
        "Content strategy for community building",
        "Retention and loyalty programs",
        "Omnichannel marketing automation"
      ]
    }
  ];

  return (
    <div className="min-h-screen noise-texture pt-32">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-brandSecondary font-medium text-sm uppercase tracking-wider" data-testid="who-label">
              Who We Work With
            </span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mt-4 mb-6" data-testid="who-heading">
              Built for
              <br />
              <span className="text-gradient">Ambitious Indian Brands</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed" data-testid="who-intro">
              We partner with founders and decision-makers who value strategic clarity, 
              measurable outcomes, and long-term growth over quick wins.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Segments */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto space-y-24">
          {segments.map((segment, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              data-testid={`segment-${idx}`}
              className="glass-card rounded-3xl p-12"
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                {/* Header */}
                <div className="space-y-4">
                  <div className="text-brandPrimary">
                    {segment.icon}
                  </div>
                  <h2 className="font-heading font-bold text-4xl text-white">
                    {segment.title}
                  </h2>
                  <p className="text-gray-400 leading-relaxed">
                    {segment.description}
                  </p>
                </div>

                {/* Challenges */}
                <div>
                  <h3 className="font-heading font-semibold text-xl text-white mb-4">
                    Common Challenges
                  </h3>
                  <ul className="space-y-3">
                    {segment.challenges.map((challenge, cIdx) => (
                      <li key={cIdx} data-testid={`segment-${idx}-challenge-${cIdx}`} className="text-gray-400 text-sm flex items-start gap-2">
                        <TrendingUp size={16} className="text-brandSecondary flex-shrink-0 mt-1" />
                        <span>{challenge}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Solutions */}
                <div>
                  <h3 className="font-heading font-semibold text-xl text-white mb-4">
                    How We Help
                  </h3>
                  <ul className="space-y-3">
                    {segment.solutions.map((solution, sIdx) => (
                      <li key={sIdx} data-testid={`segment-${idx}-solution-${sIdx}`} className="text-gray-400 text-sm flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-brandPrimary flex-shrink-0 mt-2"></div>
                        <span>{solution}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Ideal Partner Profile */}
      <section className="py-20 px-6 bg-darkPaper/50" data-testid="ideal-partner-section">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6" data-testid="ideal-partner-heading">
              You're a Great Fit If...
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              "You value strategic clarity over tactical execution alone",
              "You're ready to invest in long-term growth, not quick hacks",
              "You want a partner who understands founder challenges",
              "You believe in data-driven decision making",
              "You're open to AI tools but value human judgment",
              "You prioritize business outcomes over vanity metrics"
            ].map((criteria, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`criteria-${idx}`}
                className="glass-card rounded-2xl p-6 text-gray-300"
              >
                {criteria}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhoWeWorkWithPage;